import type { Tone } from '../components/kit'
import type { MyWorkGroup, MyWorkItem, MyWorkKind } from './api'
import { signsOnApproval } from '../components/vendor/sections'

// One status vocabulary for the whole console (release 3.2, design rule 5).
//
// # WHY ONE FILE
//
// A questionnaire sent and not started was called eight things on eight
// screens ("Sent", "Out", "Not started", "Sent, due", "With the vendor",
// "Issued", "Answering", "Awaiting your answers"), waiting for approval was
// "Awaiting", "Pending", "Waiting" and "Needs approval", and the end of a vendor
// relationship was "Signed out" on one screen and "Offboarded" on the next. A
// customer reads three words as three states and asks which one is true. Every
// screen now takes its word, its colour and its icon from here, so a state has
// one name wherever it is shown, and a screen that wants a new word has to add
// it here where the next person will see it.
//
// # THE RULES THE TABLE KEEPS
//
// - One word (or two) per state, sentence case, the same in every place.
// - A word means one state in a domain. "Accepted" was a finding state, an
//   assessment state, a clarification outcome and a document decision; each of
//   those now has its own words.
// - Colour is the third signal, never the only one: every chip carries an icon
//   and the word (WCAG 1.4.1). Red is kept for errors, rejections and the
//   critical; late is amber with a clock, so it does not read as Critical.
// - Finished states are quiet (plain), so the eye lands on what still needs
//   somebody.
// - A word shared by several domains keeps one tone and one icon: "Approved"
//   is green with a tick wherever it is shown, and "In review" is the one
//   phrase for something here being looked at.
// - A code this table does not know is shown as words, never as the raw code,
//   and the test suite fails on any stored code the table is missing.

export type StatusIcon = 'dot' | 'check' | 'clock' | 'alert' | 'pause' | 'cross' | 'send' | 'pen' | 'inbox' | 'hourglass' | 'flag' | 'exit'

export type StatusWord = { word: string; tone: Tone; icon: StatusIcon }

export type StatusDomain =
  | 'vendor'
  | 'questionnaire'
  | 'finding'
  | 'exception'
  | 'exit'
  | 'approval'
  | 'campaign'
  | 'document'
  | 'clarification'
  | 'clearance'
  | 'notice'
  | 'incident'

function w(word: string, tone: Tone, icon: StatusIcon): StatusWord {
  return { word, tone, icon }
}

// The words customers read, by domain and stored code. The single phrases that
// several domains share are named once below the table.
export const STATUS_WORDS: Record<StatusDomain, Record<string, StatusWord>> = {
  // Where the vendor is in its life with us. DRAFT is every vendor not yet
  // approved, including one whose questionnaire is out, answered and scored, so
  // it is never shown as "Draft": vendorStage() says Requested while nobody has
  // approved the request, Approved for due diligence once a TPRM lead has and
  // nothing is sent yet, and In due diligence once a questionnaire exists
  // (release 3.3: Requested > Approved for due diligence > In due diligence >
  // Awaiting approval > Active).
  vendor: {
    REQUESTED: w('Requested', 'neutral', 'dot'),
    APPROVED_FOR_DUE_DILIGENCE: w('Approved for due diligence', 'info', 'check'),
    IN_DUE_DILIGENCE: w('In due diligence', 'info', 'pen'),
    DRAFT: w('In due diligence', 'info', 'pen'),
    PENDING_APPROVAL: w('Awaiting approval', 'brand', 'hourglass'),
    ACTIVE: w('Active', 'ok', 'check'),
    SUSPENDED: w('Suspended', 'warn', 'pause'),
    OFFBOARDING: w('Offboarding', 'info', 'exit'),
    OFFBOARDED: w('Closed', 'plain', 'check'),
    REJECTED: w('Rejected', 'crit', 'cross'),
  },
  // A questionnaire (an assessment). OVERDUE is not stored: it is laid over a
  // questionnaire still with the vendor whose due date has passed.
  questionnaire: {
    NOT_SENT: w('Not sent', 'neutral', 'dot'),
    DRAFT: w('Draft', 'neutral', 'pen'),
    ISSUED: w('Sent', 'info', 'send'),
    IN_PROGRESS: w('In progress', 'info', 'pen'),
    RETURNED: w('Needs clarification', 'warn', 'alert'),
    SUBMITTED: w('Submitted', 'brand', 'inbox'),
    UNDER_REVIEW: w('In review', 'brand', 'inbox'),
    SCORED: w('Scored', 'ok', 'check'),
    ACCEPTED: w('Complete', 'plain', 'check'),
    PROVISIONAL: w('Provisional', 'special', 'flag'),
    EXPIRED: w('Expired', 'neutral', 'clock'),
    CANCELLED: w('Cancelled', 'plain', 'cross'),
    REJECTED: w('Rejected', 'crit', 'cross'),
    OVERDUE: w('Overdue', 'warn', 'clock'),
  },
  finding: {
    OPEN: w('Open', 'neutral', 'dot'),
    IN_PROGRESS: w('Remediating', 'info', 'pen'),
    ACCEPTED: w('Risk accepted', 'special', 'flag'),
    RESOLVED: w('Resolved', 'plain', 'check'),
    WITHDRAWN: w('Withdrawn', 'plain', 'cross'),
    OVERDUE: w('Overdue', 'warn', 'clock'),
  },
  // A risk acceptance (a risk exception). An approval past its expiry that the
  // worker has not yet ended is Expired too: one word for one fact.
  exception: {
    PENDING: w('Pending', 'brand', 'hourglass'),
    IN_REVIEW: w('In review', 'brand', 'inbox'),
    ESCALATED: w('Escalated', 'warn', 'alert'),
    APPROVED: w('Approved', 'ok', 'check'),
    CLOSED: w('Closed', 'plain', 'check'),
    REMEDIATION_REQUIRED: w('Changes requested', 'neutral', 'pen'),
    REJECTED: w('Rejected', 'plain', 'cross'),
    WITHDRAWN: w('Withdrawn', 'plain', 'cross'),
    EXPIRED: w('Expired', 'crit', 'clock'),
  },
  // An exit. OPEN is stored for an exit under way; the vendor is Offboarding
  // then, and the exit says the same word.
  exit: {
    NONE: w('No offboarding', 'plain', 'dot'),
    PROPOSED: w('Proposed', 'brand', 'flag'),
    OPEN: w('Offboarding', 'info', 'exit'),
    BLOCKED: w('Blocked', 'crit', 'alert'),
    OVERDUE: w('Overdue', 'warn', 'clock'),
    DUE_SOON: w('Due soon', 'warn', 'clock'),
    ALL_SIGNED: w('All signed', 'ok', 'check'),
    CLOSED: w('Closed', 'plain', 'check'),
    CALLED_OFF: w('Called off', 'plain', 'cross'),
    DISMISSED: w('Dismissed', 'plain', 'cross'),
  },
  // A second person's decision on something somebody else asked for: a tier
  // change, a vendor approval step, a bulk send.
  approval: {
    PENDING: w('Awaiting approval', 'brand', 'hourglass'),
    APPROVED: w('Approved', 'ok', 'check'),
    REJECTED: w('Rejected', 'crit', 'cross'),
    CHANGES_REQUESTED: w('Changes requested', 'neutral', 'pen'),
    WITHDRAWN: w('Withdrawn', 'plain', 'cross'),
    ACTIVE: w('Approved', 'ok', 'check'),
    SUPERSEDED: w('Replaced', 'plain', 'check'),
    ENDED: w('Ended', 'plain', 'check'),
    EXPIRED: w('Expired', 'neutral', 'clock'),
  },
  // A bulk send. A draft and a pending one both wait for a second person and
  // either can be approved from the campaign page, so they read the same.
  campaign: {
    DRAFT: w('Awaiting approval', 'brand', 'hourglass'),
    PENDING_APPROVAL: w('Awaiting approval', 'brand', 'hourglass'),
    APPROVED: w('Approved', 'ok', 'check'),
    SENDING: w('Sending', 'info', 'send'),
    SENT: w('Sent', 'plain', 'send'),
    PARTIALLY_FAILED: w('Partly failed', 'crit', 'alert'),
    CANCELLED: w('Cancelled', 'plain', 'cross'),
  },
  document: {
    VALID: w('Accepted', 'ok', 'check'),
    EXPIRING: w('Expiring soon', 'warn', 'clock'),
    EXPIRED: w('Expired', 'crit', 'clock'),
    NO_EXPIRY: w('No expiry', 'plain', 'dot'),
    AWAITING: w('In review', 'brand', 'inbox'),
    DECLINED: w('Declined', 'plain', 'cross'),
  },
  // A question asked about one answer. ANSWERED means the vendor replied and
  // somebody here has to decide the reply.
  clarification: {
    OUTSTANDING: w('Waiting on vendor', 'warn', 'hourglass'),
    ANSWERED: w('Reply received', 'brand', 'inbox'),
    ACCEPTED: w('Reply accepted', 'plain', 'check'),
    REJECTED: w('Reply not accepted', 'crit', 'cross'),
    WITHDRAWN: w('Withdrawn', 'plain', 'cross'),
  },
  clearance: {
    CLEARED: w('Cleared', 'ok', 'check'),
    PENDING_ASSESSMENT: w('Awaiting assessment', 'warn', 'hourglass'),
    BLOCKED: w('Blocked', 'crit', 'alert'),
  },
  notice: {
    UNREAD: w('New', 'info', 'dot'),
    READ: w('Read', 'plain', 'check'),
  },
  incident: {
    OPEN: w('Open', 'crit', 'alert'),
    CONTAINED: w('Contained', 'warn', 'pause'),
    CLOSED: w('Closed', 'plain', 'check'),
  },
}

// The shared phrases. "Waiting for you" replaced eleven phrasings of the same
// idea ("Needs you", "Work waiting for you", "Awaiting you", "Needs a decision"
// and the rest).
export const WAITING_FOR_YOU = 'Waiting for you'
export const AWAITING_APPROVAL = STATUS_WORDS.approval.PENDING!.word
export const LATE = STATUS_WORDS.questionnaire.OVERDUE!.word

// Words for a code nobody taught this table: sentence case, no underscores, so
// a new stored status reads as words on screen rather than as SHOUTED_CODE.
export function codeAsWords(code: string): string {
  const s = String(code ?? '')
    .replace(/[_.-]+/g, ' ')
    .trim()
    .toLowerCase()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Not recorded'
}

export function statusWord(domain: StatusDomain | string, code: string | null | undefined): StatusWord {
  const table = STATUS_WORDS[domain as StatusDomain]
  const hit = table?.[String(code ?? '').toUpperCase()]
  if (hit) return hit
  return { word: codeAsWords(String(code ?? '')), tone: 'neutral', icon: 'dot' }
}

// ------------------------------------------------------------ derived states

// The vendor's stage: the stored lifecycle, except that a vendor not yet
// approved says where its due diligence is (walk-through 4 Oct: a "Draft"
// vendor beside an "Overdue" questionnaire read as a contradiction).
//
// requestApproved is the vendor's own field (release 3.3). A request nobody has
// approved is Requested whatever else is true; approved with nothing sent is
// Approved for due diligence. Not given (a caller or an API older than the
// field), the stage reads as it did before the request step: Requested while
// nothing is sent.
export function vendorStage(status: string, questionnaireSent: boolean | null, requestApproved?: boolean): StatusWord {
  if (status === 'DRAFT') {
    if (requestApproved === false) return STATUS_WORDS.vendor.REQUESTED!
    if (questionnaireSent === false) return requestApproved ? STATUS_WORDS.vendor.APPROVED_FOR_DUE_DILIGENCE! : STATUS_WORDS.vendor.REQUESTED!
    return STATUS_WORDS.vendor.IN_DUE_DILIGENCE!
  }
  return statusWord('vendor', status)
}

// The questionnaire states still with the vendor, which can be late. One
// definition: RETURNED is with the vendor again (sent back for clarification)
// and is late on the same rule as one never answered.
export const WITH_VENDOR = new Set(['ISSUED', 'IN_PROGRESS', 'RETURNED'])
// Answers back, waiting for somebody here.
export const WAITING_ON_US = new Set(['SUBMITTED', 'UNDER_REVIEW'])
// Finished, with a score. PROVISIONAL is scored from documents and the vendor
// still has to confirm it, so it is not counted as finished.
export const FINISHED = new Set(['SCORED', 'ACCEPTED'])

export function questionnaireWord(status: string, late: boolean): StatusWord {
  if (late && WITH_VENDOR.has(status)) return STATUS_WORDS.questionnaire.OVERDUE!
  return statusWord('questionnaire', status)
}

export function findingWord(status: string, late: boolean): StatusWord {
  if (late && (status === 'OPEN' || status === 'IN_PROGRESS')) return STATUS_WORDS.finding.OVERDUE!
  return statusWord('finding', status)
}

// The word a chip shows for a stored code, with the one overlay the vocabulary
// has: Overdue laid over a questionnaire still with the vendor or a finding
// still owed. The chip draws this; anything that has to say what a chip says
// without drawing it (a tab that hides the row) asks here too.
export function chipWord(domain: StatusDomain | string, code: string | null | undefined, late?: boolean): StatusWord {
  const c = String(code ?? '')
  return domain === 'questionnaire' ? questionnaireWord(c, Boolean(late)) : domain === 'finding' ? findingWord(c, Boolean(late)) : statusWord(domain, code)
}

// Whether a status is one a reader must not miss when its row is off the page:
// drawn critical (blocked, rejected, expired, failed) or overdue. The amber
// states that are simply waiting (due soon, waiting on vendor, awaiting
// assessment) are not: a mark on a tab for those would be a mark with nothing
// wrong behind it.
export function needsSomebody(s: StatusWord): boolean {
  return s.tone === 'crit' || s.word === 'Overdue'
}

// ------------------------------------------------------------------ My work

// The one verb on each kind of row in My work, and on every link elsewhere that
// opens the same task, so a task is never "approved" on one screen and
// "decided" on the next (glossary, 7 Oct plan 2.6): Approve for a vendor
// approval and a bulk send; Decide only where the place it opens offers a yes
// and a no (the request drawer's Approve and Reject, a tier change's, a risk
// acceptance's Approve and Reject, an offboarding proposal's Start offboarding
// and Dismiss); Sign for a sign-off (an offboarding stage here, a vendor
// approval stage in taskVerb); Review for the rest. Sending the invitation
// again is "Send reminder" wherever it is offered: it was "Chase" on one
// screen and "Resend" on the next, for one control.
export const TASK_VERB: Record<MyWorkKind, string> = {
  review: 'Review answers',
  clarification: 'Review reply',
  document: 'Review document',
  chase: 'Send reminder',
  send: 'Send questionnaire',
  finding: 'Open finding',
  'vendor-approval': 'Approve',
  // The Approve request drawer offers approve and reject, so it is decided.
  'vendor-request': 'Decide',
  'tier-change': 'Decide',
  exception: 'Decide',
  'exception-review': 'Review',
  'exception-closure': 'Validate closure',
  'campaign-approval': 'Approve',
  'exit-proposal': 'Decide',
  'exit-sign': 'Sign',
  notice: 'Open',
}

// What each kind of row is, as a short noun phrase for the row's first line.
export const TASK_NOUN: Record<MyWorkKind, string> = {
  review: 'Answers to review',
  clarification: 'Reply to a question',
  document: 'Document sent by the vendor',
  chase: 'Overdue questionnaire',
  send: 'First questionnaire to send',
  finding: 'Finding',
  'vendor-approval': 'Vendor approval',
  // Two approvals, named apart (C3): the request at the start, the vendor at the end.
  'vendor-request': 'Approve request',
  'tier-change': 'Tier change',
  exception: 'Risk acceptance',
  'exception-review': 'Risk acceptance to review',
  'exception-closure': 'Risk acceptance closure',
  'campaign-approval': 'Bulk send',
  'exit-proposal': 'Offboarding proposal',
  'exit-sign': 'Offboarding sign-off',
  notice: 'Notice',
}

export const GROUP_LABEL: Record<MyWorkGroup, string> = {
  decide: 'Decide',
  review: 'Review',
  'follow-up': 'Follow up',
  notice: 'Notices',
}

// The verb a row's button carries. A late questionnaire the vendor has handed
// back for clarification cannot have its invitation sent again (the API takes
// only sent and in-progress ones), so its row opens the vendor instead. A
// vendor approval that opens the approval tab is a stage the reader signs
// there ("Sign this stage", no reject), so it is signed, as an offboarding
// stage is; one that opens the Profile admits the vendor and is approved.
export function taskVerb(item: Pick<MyWorkItem, 'kind' | 'action'>): string {
  if (item.kind === 'chase' && item.action.kind !== 'chase') return 'Open vendor'
  if (item.kind === 'vendor-approval' && signsOnApproval(item.action.href)) return TASK_VERB['exit-sign']
  return TASK_VERB[item.kind] ?? 'Open'
}
